//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { useTheme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { Tooltip } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useLocation } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { logPageView } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { DASHBOARD_MY_MESSAGES, PAGE_TITLE_EMP_MY_MESSAGES } from '../common/constants';
import { getFormattedDate, getUserDateFormat } from '../common/dateHelper';
import { loadMyMessagesById, loadThreadSummariesById } from '../common/services/myMessagesServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import AfitdTypography from '../common/elemental/AfitdTypography';
import AfitdButtonGroup from '../common/elemental/AfitdButtonGroup';
import AfitdButton from '../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import ThreadSummaries from './subComponents/ThreadSummaries';
import ThreadDetail from './subComponents/ThreadDetail';
import ThreadSummariesInternal from './subComponents/ThreadSummariesInternal';
import ThreadDetailInternal from './subComponents/ThreadDetailInternal';
// import SearchThread from './subComponents/SearchThread';
import Footer from '../dashboard/subComponents/Footer';
import SendMessageDialog from './subComponents/SendMessageDialog';
// import CreateMessageIcon from './assets/create-message.svg';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _reduce from 'lodash/reduce';
import _size from 'lodash/size';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const styles = theme => ({
    root: {
        height: '100%',
        background: '#FAFAFC',
    },
    header: {
        height: '128px',
        [theme.breakpoints.down('md')]: { height: 105 },
    },
    content: {
        flex: 1,
        overflowY: 'hidden',
        marginTop: 0,
        marginBottom: 0,
        borderRadius: 10,
        backgroundColor: '#fff',
    },
    chatRight: {
        overflowY: 'auto',
        height: '100%',
        borderLeft: '1px solid #f1f2f6',
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            background: 'none',
        },
        '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: '#F5F5F5',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0,0,0,0.2)',
        },
    },

    headerTitle: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    headerTitleContainer: {
        marginTop: '38px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerToday: {
        marginTop: 7,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    calendarIcon: { color: 'rgba(17, 30, 20, 0.6)', fontSize: 16 },
    todayContainer: {
        marginTop: '1px',
        marginLeft: '9px',
        fontSize: '13px',
        color: 'rgba(17, 30, 20, 0.6)',
    },
    title: {
        fontStyle: 'normal',
        fontWeight: '500',
        fontSize: '26px',
        lineHeight: '30px',
        color: '#111E14',
    },
    confirmedInterviewsContainer: {
        marginTop: '4px',
        marginLeft: '24px',
        display: 'flex',
    },
    unreadMessagesCount: {
        marginRight: '3px',
        color: '#FC821F',
    },
    unreadMessagesCountText: {
        marginRight: '3px',
    },
    chatLeft: {
        height: '100%',
    },
    chatLeftBlock1: {
        padding: '23px 18px 23px 22px',
    },

    createMessageIcon: {
        borderRadius: '50%',
        padding: 20,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        '&:hover': {
            background: 'rgba(0, 0, 0, 0.03)',
        },
    },
    threadSummariesHeader: {
        // display: 'flex',
        // justifyContent: 'space-between',
        // alignItems: 'center',
        padding: 20,
    },
    navigation: { margin: '10px 0' },
    navigationButtonContainer: { display: 'flex', alignItems: 'center' },
    navigationIconContainer: {
        width: '32px',
        height: '32px',
        background: '#F2F2F4',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        '&:hover': { cursor: 'pointer' },
    },
    navIconLeft: { marginLeft: 11, width: 18 },
    navigationText: { marginLeft: 8, fontSize: 16, fontWeight: 'bold', '&:hover': { cursor: 'pointer' }, marginTop: '-2px' },
    internalExternalGroupButton: {
        float: 'right',
    },
    addIcon: {
        fontSize: 45,
        padding: 10,
        borderRadius: 50,
        backgroundColor: 'rgba(0,0,0,0.03)',
        marginLeft: '10px',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: { margin: '8px 0 0 8px' },
    },
});

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        props,
        userDateFormat,
        myMessagesData,
        threadSummariesData,
        clickedThreadData,
        setClickedThreadData,
        // toggleSendMessageDialog,
        showInternalMessages,
        setShowInternalMessages,
        sendMessageDialogOpen,
        setSendMessageDialogOpen,
        isInternal,
        threadCount,
    } = params;
    const { classes } = props;

    const myMessagesId = _get(myMessagesData, 'myMessagesId');
    const currentThreadId = _get(clickedThreadData, 'threadId');
    const totalNewMessagesCount = getTotalNewMessagesCount(threadSummariesData, currentThreadId);

    const clickedThreadId = _get(clickedThreadData, 'threadId');
    const clickedThreadLastReadDt = _get(clickedThreadData, 'lastReadDt');
    const clickedThreadDeleteDt = _get(clickedThreadData, 'deleteDt');

    const defns = {
        root: { item: true, container: true, direction: 'column', lg: 12, md: 12, sm: 12, xs: 12 },
        content: { container: true, item: true, spacing: 0 },
        // chatLeft: { item: true, lg: 4, md: 6, sm: 6, xs: 6 },
        // chatRight: { item: true, lg: 8, md: 6, sm: 6, xs: 6 },
        chatLeft: { item: true, lg: 5, md: 12, sm: 12, xs: 12 },
        chatRight: { item: true, lg: 7, md: 12, sm: 12, xs: 12 },

        navigation: { container: true, direction: 'row' },
        navigationItem: { item: true, xl: 3, lg: 4, md: 11, sm: 11, xs: 11 },

        unreadMessagesCount: {
            variant: 'body2',
            value: `${totalNewMessagesCount}`,
        },
        unreadMessagesCountText: {
            variant: 'body2',
            value: 'Unread Messages',
        },
        todayText: {
            variant: 'caption',
            value: getFormattedDate(new Date(), userDateFormat),
            clsBigCaption: true,
        },
        threadSummaries: {
            data: threadSummariesData,
            threadSummaryOnClick: handleClickThreadSummary.bind(null, setClickedThreadData),
        },
        ThreadSummariesInternal: {
            data: threadSummariesData,
            threadSummaryOnClick: handleClickThreadSummary.bind(null, setClickedThreadData),
        },
        threadDetail: {
            myMessagesId,
            threadId: clickedThreadId,
            lastReadDt: clickedThreadLastReadDt,
            deleteDt: clickedThreadDeleteDt,
            threadCount,
        },
        threadDetailInternal: {
            myMessagesId,
            threadId: clickedThreadId,
            lastReadDt: clickedThreadLastReadDt,
            deleteDt: clickedThreadDeleteDt,
            isInternal,
            threadCount,
        },
        internalButton: {
            color: showInternalMessages ? 'secondary' : '',
            onClick: () => {
                setClickedThreadData(null);
                setShowInternalMessages(true);
            },
        },
        externalButton: {
            color: !showInternalMessages ? 'secondary' : '',
            onClick: () => {
                setClickedThreadData(null);
                setShowInternalMessages(false);
            },
        },
        tooltip: { title: 'Create new Internal Thread' },
        addIcon: {
            onClick: () => setSendMessageDialogOpen(true),
        },
        sendMessageDialog: {
            onClose: () => setSendMessageDialogOpen(false),
            open: sendMessageDialogOpen,
            // conversations,
            // limitExceeded: engagementLimit,
            // jobSeekerId,
            // jobSeekerName,
            // jobSeekerProfilePicUrl,
        },

        // createMessageIcon: {
        //     onClick: toggleSendMessageDialog,
        // },
        // sendMessageDialog: {
        //     conversations,
        //     onClose: toggleSendMessageDialog,
        //     open: sendMessageDialogOpen,
        // },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickThreadSummary = (setClickedThreadData, clickedThreadData, currentThreadId) => {
    const threadId = _get(clickedThreadData, 'threadId');
    if (currentThreadId !== threadId) {
        setClickedThreadData(clickedThreadData);
    }
};

const getTotalNewMessagesCount = (data, currentThreadId) => {
    return _reduce(data, (total, thread) => (_get(thread, 'threadId') === currentThreadId ? total : total + _get(thread, 'unreadCount', 0)), 0);
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const MyMessages = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    const employerId = _get(appContext, 'employerId');
    const userId = _get(appContext, 'userId');
    const currentEmployer = _get(appContext, 'currentEmployer');
    const tier = _get(currentEmployer, 'tier');
    const location = useLocation();
    const setCurrentThreadId = _get(context, 'setCurrentThreadId');
    const userDateFormat = getUserDateFormat(appContext);

    const theme = useTheme();
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [myMessagesData, setMyMessagesData] = useState([]);
    const [threadSummariesData, setThreadSummariesData] = useState();
    const [clickedThreadData, setClickedThreadData] = useState();
    const [showMessageDetails, setShowMessageDetails] = useState(false);
    const [showInternalMessages, setShowInternalMessages] = useState(false);
    const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_EMP_MY_MESSAGES);
    }, []);

    useEffect(() => {
        if (employerId) {
            // load myMessages for employer
            const unsubscribeLoadMyMessagesByIdOnSnapshot = loadMyMessagesById(showInternalMessages ? userId : employerId, setMyMessagesData);

            // load all thread summaries for employer
            const unsubscribeLoadThreadSummariesByIdOnSnapshot = loadThreadSummariesById(showInternalMessages ? userId : employerId, setThreadSummariesData);

            return () => {
                unsubscribeRealtimeUpdates([unsubscribeLoadMyMessagesByIdOnSnapshot, unsubscribeLoadThreadSummariesByIdOnSnapshot]);
            };
        }
    }, [employerId, showInternalMessages, userId]);

    // useEffect(() => {
    //     // set initial clickedThreadData in order to load the messages of the first thread
    //     if (threadSummariesData && !clickedThreadData) {
    //         const firstThreadSummaryData = _get(threadSummariesData, '[0]');
    //         setClickedThreadData(firstThreadSummaryData);
    //     }
    // }, [threadSummariesData, clickedThreadData]);

    useEffect(() => {
        const { state } = location;
        if (state?.state?.threadId) {
            const threadFound = threadSummariesData.find(el => el.threadId === state?.state?.threadId);
            setClickedThreadData(threadFound);
        }
    }, [threadSummariesData, location]);

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const clearCurrentThread = useCallback(() => {
        setClickedThreadData(null);
        setCurrentThreadId(null);
    }, [setCurrentThreadId]);

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const threadCount = _size(threadSummariesData);

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        userDateFormat,
        myMessagesData,
        threadSummariesData,
        clickedThreadData,
        setClickedThreadData,
        showInternalMessages,
        setShowInternalMessages,
        sendMessageDialogOpen,
        setSendMessageDialogOpen,
        isInternal: showInternalMessages,
        threadCount,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, clearCurrentThread }}>
            <AfitdPageHead title={PAGE_TITLE_EMP_MY_MESSAGES} />
            <Grid {...defns.root}>
                {showMessageDetails && mdMatches ? (
                    smMatches ? null : (
                        <Grid {...defns.navigation}>
                            <Grid {...defns.navigationItem}>
                                <div {...defns.navigationButtonContainer} onClick={() => setShowMessageDetails(false)}>
                                    <div {...defns.navigationIconContainer}>
                                        <ArrowBackIosIcon {...defns.navIconLeft} />
                                    </div>
                                    <span {...defns.navigationText}>Back</span>
                                </div>
                            </Grid>
                        </Grid>
                    )
                ) : (
                    <Grid {...defns.header}>
                        <div {...defns.headerTitleContainer}>
                            <div {...defns.headerTitle}>
                                <div {...defns.title}>{DASHBOARD_MY_MESSAGES}</div>
                                {tier > 1 && showInternalMessages && (
                                    <Tooltip {...defns.tooltip}>
                                        <AddIcon {...defns.addIcon} />
                                    </Tooltip>
                                )}
                                <div {...defns.confirmedInterviewsContainer}>
                                    <AfitdTypography {...defns.unreadMessagesCount} />
                                    <AfitdTypography {...defns.unreadMessagesCountText} />
                                </div>
                            </div>
                            {tier > 1 && (
                                <div>
                                    <AfitdButtonGroup variant="contained" aria-label="contained button group" {...defns.internalExternalGroupButton}>
                                        <AfitdButton isGroup={true} {...defns.internalButton}>
                                            INTERNAL
                                        </AfitdButton>
                                        <AfitdButton isGroup={true} {...defns.externalButton}>
                                            EXTERNAL
                                        </AfitdButton>
                                    </AfitdButtonGroup>
                                </div>
                            )}
                        </div>
                        <div {...defns.headerToday}>
                            <CalendarTodayIcon {...defns.calendarIcon} />
                            <div {...defns.todayContainer}>
                                <AfitdTypography {...defns.todayText} />
                            </div>
                        </div>
                    </Grid>
                )}
                {showInternalMessages ? (
                    //! internal Messages
                    <Grid {...defns.content}>
                        {showMessageDetails && mdMatches ? (
                            <Grid {...defns.chatRight}>
                                <ThreadDetailInternal {...defns.threadDetailInternal} setShowMessageDetails={setShowMessageDetails} />
                            </Grid>
                        ) : (
                            <Grid {...defns.chatLeft}>
                                {/* <div {...defns.threadSummariesHeader}>
                                    <SearchThread />
                                </div> */}
                                <br />
                                <ThreadSummariesInternal {...defns.ThreadSummariesInternal} setShowMessageDetails={setShowMessageDetails} />
                            </Grid>
                        )}
                        {/* chat details hide for 0-1023px (mdDown) */}
                        {mdMatches ? null : (
                            <Grid {...defns.chatRight}>
                                <ThreadDetailInternal {...defns.threadDetailInternal} setShowMessageDetails={setShowMessageDetails} />
                            </Grid>
                        )}
                    </Grid>
                ) : (
                    //! External Messages
                    <Grid {...defns.content}>
                        {showMessageDetails && mdMatches ? (
                            <Grid {...defns.chatRight}>
                                <ThreadDetail {...defns.threadDetail} setShowMessageDetails={setShowMessageDetails} />
                            </Grid>
                        ) : (
                            <Grid {...defns.chatLeft}>
                                {/* <div {...defns.threadSummariesHeader}>
                                    <SearchThread />
                                </div> */}
                                <br />
                                <ThreadSummaries {...defns.threadSummaries} setShowMessageDetails={setShowMessageDetails} />
                            </Grid>
                        )}
                        {/* chat details hide for 0-1023px (mdDown) */}
                        {mdMatches ? null : (
                            <Grid {...defns.chatRight}>
                                <ThreadDetail {...defns.threadDetail} setShowMessageDetails={setShowMessageDetails} />
                            </Grid>
                        )}
                    </Grid>
                )}
                <Grid {...defns.bottomFooter}>{mdMatches ? <Footer /> : null}</Grid>
            </Grid>
            {sendMessageDialogOpen && <SendMessageDialog {...defns.sendMessageDialog} />}
        </LayoutContext.Provider>
    );
    //---------------------------------------------------------------------
};

export default withStyles(styles)(MyMessages);
